import { ShadcnButton, ShadcnDrawer } from '@shadcn-features/core';
import React, {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useMemo,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import PdfViewer from '@shared/components/PdfViewer';
import { useQueryMutation } from '@shared/hooks/useQueryMutation';
import ProSpinner from '@shared/newComponents/ProSpinner';
import { FileUploadFlowVM } from '@shared/types/process';

export type PreviewDocumentRef = {
    trigger: (documentIdArg: string) => void;
};

interface Props {
    onBeforeTrigger?: () => Promise<unknown>;
}
const InnerPreviewDocument = (
    { onBeforeTrigger, ...props }: Props,
    ref: React.Ref<PreviewDocumentRef>,
) => {
    const { t } = useTranslation();

    const [open, setOpen] = useState(false);
    const [pdfFile, setPdfFile] = useState<string>();

    const getDocumentMutation = useQueryMutation<string, FileUploadFlowVM>(
        `flow/Files/GetBase64/{id}`,
        'get',
        {
            variablesOption(url, variables, axiosConfig) {
                return {
                    url: url.replace('{id}', variables),
                    axiosConfig: axiosConfig,
                };
            },
        },
    );

    useImperativeHandle(ref, () => ({
        async trigger(documentIdArg) {
            const dataFile =
                await getDocumentMutation.mutateAsync(documentIdArg);
            if (dataFile.content) {
                handelOnClick(dataFile.content);
            }
        },
    }));

    const showFile = useMemo(() => {
        if (pdfFile) return pdfFile;
    }, [pdfFile]);

    const handelOnClick = useCallback(
        async (base64: string) => {
            try {
                await onBeforeTrigger?.();
                if (base64) {
                    setPdfFile(base64);
                    setOpen(true);
                } else {
                    throw new Error('base64 not found');
                }
            } catch (error) {
                setOpen(false);
                console.log(error);
            }
        },
        [onBeforeTrigger],
    );

    return (
        <ProSpinner spinning={false}>
            <ShadcnDrawer
                hideCloseButton
                open={open}
                title={t('translation:documentPreview')}
                width={'100vw'}
                extra={
                    <div className='flex items-center gap-4'>
                        <ShadcnButton
                            size={'sm'}
                            variant={'destructive'}
                            onClick={() => setOpen(false)}
                        >
                            {t('translation:cancel')}
                        </ShadcnButton>
                    </div>
                }
                onClose={() => setOpen(false)}
                {...props}
            >
                {showFile && <PdfViewer loading={!showFile} pdf={showFile} />}
            </ShadcnDrawer>
        </ProSpinner>
    );
};

export const PreviewDocument = forwardRef(InnerPreviewDocument) as (
    props: Props & { ref?: React.Ref<PreviewDocumentRef> },
) => ReturnType<typeof InnerPreviewDocument>;
